import React, { useState, useEffect, useRef } from 'react';
import { Typography } from '../../../interfaces/typography';
import { CommentDataResult, SessionDataResult } from "interfaces/sessions";
import { useCommentFunctions } from '../../../lib/redux/sessions/data/comment-hook';
import { MessageSquare, X, Send, Edit2, Trash2, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { fromReduxSafeSessionDataResult } from '../../../lib/redux/sessions/data/data-slice';

interface CommentPanelProps {
  sessionId: string;
  isOpen: boolean;
  onToggle: () => void;
  onScrollToTranscript?: (transcriptIdx: number) => void;
}

export const CommentPanel: React.FC<CommentPanelProps> = ({
  sessionId,
  isOpen,
  onToggle,
  onScrollToTranscript
}) => {
  const data = useSelector((state: RootState) => state.sessionDetails.sessionIdToDetails);
  const sessionData = data[sessionId]
  if (!sessionData || !sessionData.data) return null
  
  // Convert Redux-safe data back to original format
  const convertedSessionData = fromReduxSafeSessionDataResult(sessionData.data);
  
  return (
    <CommentPanelImpl
      sessionData={convertedSessionData}
      isOpen={isOpen}
      onToggle={onToggle}
      onScrollToTranscript={onScrollToTranscript}
    />
  );
}

interface CommentSubPanelProps {
    sessionData: SessionDataResult;
    isOpen: boolean;
    onToggle: () => void;
    onScrollToTranscript?: (transcriptIdx: number) => void;
  }

const CommentPanelImpl: React.FC<CommentSubPanelProps> = ({
  sessionData,
  isOpen,
  onToggle,
  onScrollToTranscript
}) => {
  const { addAndSaveComment, removeComment, updateCommentText, saveComment } = useCommentFunctions();
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [editText, setEditText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  
  // Get current user ID
  const currentUser = useSelector((state: RootState) => state.user.value);


  // Sort comments: general comments first, then by transcript index
  const sortedComments = [...(sessionData.comments || [])].sort((a, b) => {
    // General comments (transcriptIdx === -1) go to the top
    if (a.transcriptIdx === -1 && b.transcriptIdx !== -1) return -1;
    if (a.transcriptIdx !== -1 && b.transcriptIdx === -1) return 1;
    
    // Then sort by transcript index
    return (a.transcriptIdx ?? 0) - (b.transcriptIdx ?? 0);
  });

  // Focus on the edit input when editing starts
  useEffect(() => {
    if (editingCommentId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingCommentId]);

  // Focus on the comment input when the panel opens
  useEffect(() => {
    if (isOpen && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [isOpen]);

  const handleAddGeneralComment = async () => {
    if (commentText.trim() === '') return;
    
    setIsSaving(true);
    try {
      // Use addAndSaveComment directly
      await addAndSaveComment(sessionData.session.session_id, undefined, commentText);
      setCommentText('');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEditing = (comment: CommentDataResult) => {
    // Only allow editing if the user owns the comment
    if (comment.user_id === currentUser?.user_id) {
      setEditingCommentId(comment.comment_id);
      setEditText(comment.comment);
    }
  };

  const handleSaveEdit = async (commentId: string) => {
    if (editText.trim() === '') {
      handleDeleteComment(commentId);
      return;
    }
    
    setIsSaving(true);
    try {
      // Update comment text and save immediately
      const success = await updateCommentText(sessionData.session.session_id, commentId, editText);
      if (success) {
        await saveComment(sessionData.session.session_id, commentId);
      }
    } finally {
      setIsSaving(false);
      setEditingCommentId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
  };

  const handleDeleteComment = async (commentId: string) => {
    // Find the comment to check ownership
    const comment = sessionData.comments?.find(c => c.comment_id === commentId);
    if (comment && comment.user_id === currentUser?.user_id) {
      await removeComment(sessionData.session.session_id, commentId);
    }
  };

  const formatTimestamp = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = (seconds % 60).toFixed(0);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getTranscriptText = (transcriptIdx: number): string => {
    if (transcriptIdx === -1 || !sessionData.turns || !sessionData.turns[transcriptIdx]) {
      return '';
    }
    
    const text = sessionData.turns[transcriptIdx].text;
    // Truncate long text to 100 characters with ellipsis
    return text.length > 100 ? `${text.substring(0, 100)}...` : text;
  };

  const isCommentOwner = (comment: CommentDataResult): boolean => {
    return comment.user_id === currentUser?.user_id;
  };

  const formatCommentDate = (date: Date): string => {
    return new Date(date).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  // Filter to only show general comments in the count
  const generalComments = sortedComments.filter(comment => comment.transcriptIdx === undefined);
  const transcriptComments = sortedComments.filter(comment => comment.transcriptIdx !== undefined);
  const hasGeneralComments = generalComments.length > 0;
  const hasTranscriptComments = transcriptComments.length > 0;
  const totalComments = sortedComments.length;


  // Jump to transcript function
  const scrollToTranscript = (transcriptIdx: number) => {
    // Close the panel
    onToggle();
    
    // Call the parent component's scroll function if provided
    if (onScrollToTranscript) {
      onScrollToTranscript(transcriptIdx);
    }
  };

  // Get comment status class
  const getCommentStatusClass = (comment: CommentDataResult) => {
    if (comment.isPending) {
      return 'border-amber-200 bg-amber-50';
    } else {
      return 'border-gray-200';
    }
  };

  return (
    <>
      {/* Toggle button that's always visible */}
      <button
        onClick={onToggle}
        className={`fixed right-0 top-1/2 transform -translate-y-1/2 z-40
          bg-white shadow-md rounded-l-lg p-2 flex items-center justify-center
          hover:bg-gray-50 transition-colors`}
        aria-label={isOpen ? 'Close comments panel' : 'Open comments panel'}
        title="Comments"
      >
        {isOpen ? <X size={20} /> : <MessageSquare size={20} />}
        {!isOpen && totalComments > 0 && (
          <div className="absolute top-0 right-0 -mt-1 -mr-1">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-xs text-white">
              {totalComments}
            </span>
          </div>
        )}
      </button>

      {/* Comment panel */}
      { /* @ts-ignore */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: 400 }}
            animate={{ x: 0 }}
            exit={{ x: 400 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed right-0 top-0 bottom-0 w-96 bg-white shadow-lg z-30 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <MessageSquare size={20} className="text-indigo-600" />
                <Typography variant="h3">
                  All Comments ({totalComments})
                </Typography>
              </div>
              <button
                onClick={onToggle}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close comments panel"
              >
                <X size={18} />
              </button>
            </div>

            {/* Comment list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {totalComments === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <MessageSquare size={24} className="text-gray-400" />
                  </div>
                  <Typography variant="mediumParagraph" color="gray">
                    No comments yet
                  </Typography>
                  <Typography variant="smallParagraph" color="gray" className="mt-2">
                    Add a general comment about the entire call below
                  </Typography>
                </div>
              ) : (
                <>
                  {/* General Comments Section */}
                  {hasGeneralComments && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-px bg-gray-200 flex-grow"></div>
                        <Typography variant="smallParagraph" color="gray" className="font-medium">
                          General Comments
                        </Typography>
                        <div className="h-px bg-gray-200 flex-grow"></div>
                      </div>
                      
                      <div className="space-y-3">
                        {generalComments.map((comment) => (
                          <div 
                            key={comment.comment_id} 
                            className={`rounded-lg border p-3 ${getCommentStatusClass(comment)}`}
                          >
                            {/* Comment metadata */}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-1.5">
                                <Typography variant="smallParagraph" className="font-medium">
                                  {comment.user_name}
                                </Typography>
                                {comment.isPending && (
                                  <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full flex items-center">
                                    <Clock size={10} className="mr-0.5" />
                                    Pending
                                  </span>
                                )}
                              </div>
                              <Typography variant="caption" color="gray">
                                {formatCommentDate(comment.created_at)}
                              </Typography>
                            </div>

                            {/* Comment content */}
                            {editingCommentId === comment.comment_id ? (
                              <div className="mt-2">
                                <textarea
                                  ref={editInputRef}
                                  value={editText}
                                  onChange={(e) => setEditText(e.target.value)}
                                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                  rows={3}
                                  placeholder="Edit your comment..."
                                />
                                <div className="flex justify-end gap-2 mt-2">
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => handleSaveEdit(comment.comment_id)}
                                    disabled={isSaving}
                                    className={`px-3 py-1 text-sm ${
                                      isSaving 
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                    } rounded`}
                                  >
                                    {isSaving ? 'Saving...' : 'Save'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="relative group">
                                <Typography variant="mediumParagraph">
                                  {comment.comment}
                                </Typography>
                                {/* Only show edit/delete buttons if the user owns the comment */}
                                {isCommentOwner(comment) && (
                                  <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                    <button
                                      onClick={() => handleStartEditing(comment)}
                                      className="p-1 text-gray-500 hover:text-indigo-600 hover:bg-gray-100 rounded-full"
                                      aria-label="Edit comment"
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteComment(comment.comment_id)}
                                      className="p-1 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded-full"
                                      aria-label="Delete comment"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Transcript Comments Section */}
                  {hasTranscriptComments && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-px bg-gray-200 flex-grow"></div>
                        <Typography variant="smallParagraph" color="gray" className="font-medium">
                          Transcript Comments
                        </Typography>
                        <div className="h-px bg-gray-200 flex-grow"></div>
                      </div>
                      
                      <div className="space-y-4">
                        {transcriptComments.map((comment) => (
                          <div 
                            key={comment.comment_id} 
                            className={`rounded-lg border p-3 ${getCommentStatusClass(comment)}`}
                          >
                            {/* Transcript reference */}
                            {comment.transcriptIdx !== undefined && comment.transcriptIdx !== -1 && (
                              <div 
                                className="mb-2 p-2 bg-gray-50 rounded text-sm text-gray-600 italic border-l-2 border-gray-300 cursor-pointer hover:bg-gray-100"
                                onClick={() => scrollToTranscript(comment.transcriptIdx!)}
                                title="Click to jump to this transcript"
                              >
                                <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                                  <Clock size={12} />
                                  {comment.start !== null && (
                                    <span>
                                      {formatTimestamp(comment.start / 1000)}
                                      {comment.end && ` - ${formatTimestamp(comment.end / 1000)}`}
                                    </span>
                                  )}
                                </div>
                                "{getTranscriptText(comment.transcriptIdx)}"
                              </div>
                            )}

                            {/* Comment metadata */}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-1.5">
                                <Typography variant="smallParagraph" className="font-medium">
                                  {comment.user_name}
                                </Typography>
                                {comment.isPending && (
                                  <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full flex items-center">
                                    <Clock size={10} className="mr-0.5" />
                                    Pending
                                  </span>
                                )}
                              </div>
                              <Typography variant="caption" color="gray">
                                {formatCommentDate(comment.created_at)}
                              </Typography>
                            </div>

                            {/* Comment content */}
                            {editingCommentId === comment.comment_id ? (
                              <div className="mt-2">
                                <textarea
                                  ref={editInputRef}
                                  value={editText}
                                  onChange={(e) => setEditText(e.target.value)}
                                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                  rows={3}
                                  placeholder="Edit your comment..."
                                />
                                <div className="flex justify-end gap-2 mt-2">
                                  <button
                                    onClick={handleCancelEdit}
                                    className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => handleSaveEdit(comment.comment_id)}
                                    disabled={isSaving}
                                    className={`px-3 py-1 text-sm ${
                                      isSaving 
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                    } rounded`}
                                  >
                                    {isSaving ? 'Saving...' : 'Save'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="relative group">
                                <Typography variant="mediumParagraph">
                                  {comment.comment}
                                </Typography>
                                {/* Only show edit/delete buttons if the user owns the comment */}
                                {isCommentOwner(comment) && (
                                  <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                    <button
                                      onClick={() => handleStartEditing(comment)}
                                      className="p-1 text-gray-500 hover:text-indigo-600 hover:bg-gray-100 rounded-full"
                                      aria-label="Edit comment"
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteComment(comment.comment_id)}
                                      className="p-1 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded-full"
                                      aria-label="Delete comment"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Add comment input */}
            <div className="p-4 border-t">
              <div className="flex flex-col">
                <textarea
                  ref={commentInputRef}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                  placeholder="Add a general comment about the entire call..."
                />
                <div className="flex justify-end mt-3">
                  <button
                    onClick={handleAddGeneralComment}
                    disabled={commentText.trim() === '' || isSaving}
                    className={`px-4 py-2 rounded-md flex items-center gap-1 ${
                      commentText.trim() === '' || isSaving
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    <Send size={16} />
                    {isSaving ? 'Saving...' : 'Add Comment'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}; 